/**
 * Shared orchestration for EntityUnifiedModal feature wrappers:
 * tab resolution, legacy edit-tab remap, form footer visibility.
 */

export interface ResolveUnifiedModalTabOptions<TTab extends string> {
  readonly activeTab: TTab | string;
  readonly tabIds: readonly string[];
  readonly isCreate: boolean;
  readonly showOverview: boolean;
  readonly createDefaultTab: TTab;
  readonly overviewTab: TTab;
  /** Stale host ids (e.g. details, edit) mapped to domain edit tab when present in tabIds. */
  readonly legacyEditTabIds?: readonly string[];
  readonly legacyEditRemapTab?: TTab;
  readonly fallbackEditTab?: TTab;
}

/**
 * Resolves active tab when host state is stale or tab list is entitlement-gated.
 */
export function resolveUnifiedModalTab<TTab extends string>(
  options: ResolveUnifiedModalTabOptions<TTab>,
): TTab {
  const {
    activeTab,
    tabIds,
    isCreate,
    showOverview,
    createDefaultTab,
    overviewTab,
    legacyEditTabIds = ['details', 'edit'],
    legacyEditRemapTab,
    fallbackEditTab,
  } = options;

  const activeTabId = String(activeTab);

  if (isCreate && activeTabId === overviewTab) {
    return createDefaultTab;
  }

  const legacyRemap = legacyEditRemapTab ?? fallbackEditTab ?? createDefaultTab;
  if (legacyEditTabIds.includes(activeTabId) && tabIds.includes(legacyRemap)) {
    return legacyRemap;
  }

  if (!tabIds.includes(activeTabId)) {
    if (showOverview && tabIds.includes(overviewTab)) {
      return overviewTab;
    }
    const editFallback = fallbackEditTab ?? createDefaultTab;
    if (tabIds.includes(editFallback)) {
      return editFallback;
    }
    return (tabIds[0] as TTab | undefined) ?? overviewTab;
  }

  return activeTab as TTab;
}

export function mapTabToFormSection<TTab extends string, TSection extends string>(
  tab: TTab,
  config: {
    readonly nonFormTabs: readonly TTab[];
    readonly sectionByTab?: Partial<Record<TTab, TSection>>;
  },
): TSection | null {
  if (config.nonFormTabs.includes(tab)) {
    return null;
  }
  const mapped = config.sectionByTab?.[tab];
  if (mapped !== undefined) {
    return mapped;
  }
  return tab as unknown as TSection;
}

export function shouldShowUnifiedFormFooter(
  canShowForm: boolean,
  formSection: string | null,
): boolean {
  return canShowForm && formSection !== null;
}

/** Active create-wizard step index (0-based); unknown id → 0. */
export function resolveCreateWizardStepIndex(
  stepIds: readonly string[],
  activeId: string,
): number {
  const idx = stepIds.indexOf(activeId);
  return idx >= 0 ? idx : 0;
}

/** Adjacent create-wizard step id, or null at the rail edge. */
export function adjacentCreateWizardStep(
  stepIds: readonly string[],
  activeId: string,
  direction: 'back' | 'next',
): string | null {
  const idx = resolveCreateWizardStepIndex(stepIds, activeId);
  const nextIdx = direction === 'back' ? idx - 1 : idx + 1;
  return stepIds[nextIdx] ?? null;
}
